/**
 * Pyramid Import Service
 * 
 * Handles importing items from Excel/CSV files into the Max Priorities Pyramid.
 * Also provides template download functionality for the expected format.
 */

import * as XLSX from 'xlsx';
import {
  type TimeHorizon,
  type PyramidConfig,
  type PyramidItem,
  type PyramidImportResult,
  type PyramidCell,
  type StrategicTheme,
  TIME_HORIZONS_ORDERED,
  DEFAULT_STRATEGIC_THEMES,
  parseTimeHorizon,
  getRowByTimeHorizon,
  createEmptyPyramidGrid,
  createDefaultPyramidConfig,
} from '../types/prioritization/pyramid';

// ============================================================================
// Types
// ============================================================================

export interface PyramidExcelRow {
  title: string;
  description?: string;
  timeHorizon: string;
  strategicTheme: string;
  order?: number;
}

export interface PyramidImportOptions {
  /** Custom themes to use (if not using template defaults) */
  customThemes?: string[];
  
  /** Whether to create themes from unique values in the data */
  autoDetectThemes?: boolean;
  
  /** Skip rows with invalid time horizons vs throwing error */
  skipInvalidRows?: boolean;
}

// ============================================================================
// Template Generation
// ============================================================================

/**
 * Generate the Excel template for pyramid import
 */
export function generatePyramidTemplate(): Uint8Array {
  const workbook = XLSX.utils.book_new();
  
  // Sheet 1: Items (empty template with headers and example rows)
  // Examples show multiple items in same cell with different order values
  const itemsData = [
    {
      'Title': 'Example: Automate deployment pipeline',
      'Description': 'Set up CI/CD for faster releases',
      'Time Horizon': 'Get House in Order',
      'Strategic Theme': 'Process Automation',
      'Order': 1, // Highest priority in this cell
    },
    {
      'Title': 'Example: Automate testing suite',
      'Description': 'Add comprehensive automated test coverage',
      'Time Horizon': 'Get House in Order',
      'Strategic Theme': 'Process Automation',
      'Order': 2, // Second priority in same cell
    },
    {
      'Title': 'Example: Reduce database latency',
      'Description': 'Optimize queries and add caching layer',
      'Time Horizon': 'Reinforce Foundation',
      'Strategic Theme': 'Tech Debt',
      'Order': 1,
    },
    {
      'Title': 'Example: Refactor authentication module',
      'Description': 'Clean up legacy auth code',
      'Time Horizon': 'Reinforce Foundation',
      'Strategic Theme': 'Tech Debt',
      'Order': 2,
    },
    {
      'Title': 'Example: Zero critical bugs by Q4',
      'Description': 'Comprehensive bug bash and prevention measures',
      'Time Horizon': 'Create the Future',
      'Strategic Theme': 'Defects',
      'Order': 1,
    },
  ];
  
  const itemsSheet = XLSX.utils.json_to_sheet(itemsData);
  
  // Set column widths
  itemsSheet['!cols'] = [
    { wch: 40 }, // Title
    { wch: 60 }, // Description
    { wch: 25 }, // Time Horizon
    { wch: 25 }, // Strategic Theme
    { wch: 10 }, // Order
  ];
  
  XLSX.utils.book_append_sheet(workbook, itemsSheet, 'Items');
  
  // Sheet 2: Time Horizons (reference)
  const horizonsData = [
    { 
      'Time Horizon': 'Create the Future',
      'Aliases': 'future, strategic, long_term, planning',
      'Description': 'Long-term strategic priorities (top row)',
    },
    { 
      'Time Horizon': 'Reinforce Foundation',
      'Aliases': 'foundation, middle, scoping, growth',
      'Description': 'Connecting layer bridging now and future (middle row)',
    },
    { 
      'Time Horizon': 'Get House in Order',
      'Aliases': 'now, immediate, tactical, executing',
      'Description': 'Near-term and immediate priorities (bottom row)',
    },
  ];
  
  const horizonsSheet = XLSX.utils.json_to_sheet(horizonsData);
  horizonsSheet['!cols'] = [
    { wch: 25 },
    { wch: 40 },
    { wch: 50 },
  ];
  XLSX.utils.book_append_sheet(workbook, horizonsSheet, 'Time Horizons');
  
  // Sheet 3: Strategic Themes (customizable)
  const themesData = DEFAULT_STRATEGIC_THEMES.map((theme, idx) => ({
    'Theme Name': theme,
    'Column Order': idx + 1,
    'Description': `Items related to ${theme.toLowerCase()}`,
  }));
  
  const themesSheet = XLSX.utils.json_to_sheet(themesData);
  themesSheet['!cols'] = [
    { wch: 25 },
    { wch: 15 },
    { wch: 40 },
  ];
  XLSX.utils.book_append_sheet(workbook, themesSheet, 'Strategic Themes');
  
  // Sheet 4: Instructions
  const instructionsData = [
    { 'Instructions': 'How to use this template:' },
    { 'Instructions': '' },
    { 'Instructions': '1. Add your items to the "Items" sheet' },
    { 'Instructions': '2. Each item needs a Title, Time Horizon, and Strategic Theme' },
    { 'Instructions': '3. Description is optional but recommended' },
    { 'Instructions': '4. Order is optional - use it to rank items within the same cell' },
    { 'Instructions': '' },
    { 'Instructions': 'Time Horizon values (use one of these):' },
    { 'Instructions': '  - "Create the Future" (or: future, strategic, planning)' },
    { 'Instructions': '  - "Reinforce Foundation" (or: foundation, scoping, growth)' },
    { 'Instructions': '  - "Get House in Order" (or: now, immediate, executing)' },
    { 'Instructions': '' },
    { 'Instructions': 'Strategic Theme values:' },
    { 'Instructions': '  - Use themes from the "Strategic Themes" sheet' },
    { 'Instructions': '  - You can customize the themes for your organization' },
    { 'Instructions': '  - New themes will be created automatically on import' },
    { 'Instructions': '' },
    { 'Instructions': 'Order column:' },
    { 'Instructions': '  - Use to set priority order WITHIN each cell' },
    { 'Instructions': '  - Order 1 = highest priority in that cell (top of stack)' },
    { 'Instructions': '  - Order 2, 3, ... = descending priority' },
    { 'Instructions': '  - Items with same Time Horizon + Strategic Theme are grouped' },
    { 'Instructions': '  - If Order is blank, items are sorted by their row order in the file' },
    { 'Instructions': '' },
    { 'Instructions': 'Tips:' },
    { 'Instructions': '  - Delete the example rows before importing' },
    { 'Instructions': '  - Items will be placed in grid cells based on their horizon and theme' },
    { 'Instructions': '  - You can have multiple items in the same cell - use Order to rank them' },
  ];
  
  const instructionsSheet = XLSX.utils.json_to_sheet(instructionsData, { skipHeader: true });
  instructionsSheet['!cols'] = [{ wch: 70 }];
  XLSX.utils.book_append_sheet(workbook, instructionsSheet, 'Instructions');
  
  // Generate binary
  const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
  return new Uint8Array(buffer);
}

/**
 * Trigger download of the pyramid template
 */
export function downloadPyramidTemplate(): void {
  const data = generatePyramidTemplate();
  const blob = new Blob([data], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = 'max-priorities-pyramid-template.xlsx';
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

// ============================================================================
// Import Functions
// ============================================================================

/**
 * Parse an Excel/CSV file and extract pyramid items
 */
export async function importFromExcel(
  file: File,
  options: PyramidImportOptions = {}
): Promise<PyramidImportResult> {
  const { customThemes, autoDetectThemes = true, skipInvalidRows = true } = options;
  
  try {
    // Read the file
    const arrayBuffer = await file.arrayBuffer();
    const workbook = XLSX.read(arrayBuffer, { type: 'array' });
    
    // Find the Items sheet (try various names)
    const itemsSheetName = workbook.SheetNames.find(name => 
      ['items', 'data', 'priorities', 'sheet1'].includes(name.toLowerCase())
    ) || workbook.SheetNames[0];
    
    const worksheet = workbook.Sheets[itemsSheetName];
    if (!worksheet) {
      return {
        success: false,
        config: createDefaultPyramidConfig('Import', 'system'),
        items: [],
        grid: [],
        summary: { totalItems: 0, byTimeHorizon: {} as Record<TimeHorizon, number>, byTheme: {} },
        error: 'No valid sheet found in the file',
      };
    }
    
    // Parse to JSON
    const rawData = XLSX.utils.sheet_to_json<Record<string, unknown>>(worksheet, { defval: '' });
    
    if (rawData.length === 0) {
      return {
        success: false,
        config: createDefaultPyramidConfig('Import', 'system'),
        items: [],
        grid: [],
        summary: { totalItems: 0, byTimeHorizon: {} as Record<TimeHorizon, number>, byTheme: {} },
        error: 'No data rows found in the file',
      };
    }
    
    // Normalize column names
    const normalizedData = normalizeColumnNames(rawData);
    
    // Extract themes from data if auto-detecting
    let themes: string[] = customThemes || DEFAULT_STRATEGIC_THEMES;
    if (autoDetectThemes && !customThemes) {
      const detectedThemes = new Set<string>();
      for (const row of normalizedData) {
        if (row.strategicTheme && typeof row.strategicTheme === 'string') {
          detectedThemes.add(row.strategicTheme.trim());
        }
      }
      if (detectedThemes.size > 0) {
        themes = Array.from(detectedThemes);
      }
    }
    
    // Create pyramid config
    const config = createDefaultPyramidConfig(
      file.name.replace(/\.[^/.]+$/, ''),
      'import',
      themes
    );
    
    // Parse items
    const items: PyramidItem[] = [];
    const warnings: string[] = [];
    
    for (let i = 0; i < normalizedData.length; i++) {
      const row = normalizedData[i];
      const rowNum = i + 2; // Excel row number (1-indexed + header row)
      
      // Skip empty titles
      const title = String(row.title || '').trim();
      if (!title) {
        if (!skipInvalidRows) {
          warnings.push(`Row ${rowNum}: Missing title`);
        }
        continue;
      }
      
      // Skip example rows
      if (title.toLowerCase().startsWith('example:')) {
        continue;
      }
      
      // Parse time horizon
      const timeHorizonRaw = String(row.timeHorizon || '').trim();
      const timeHorizon = parseTimeHorizon(timeHorizonRaw);
      
      if (!timeHorizon) {
        if (skipInvalidRows) {
          warnings.push(`Row ${rowNum}: Invalid time horizon "${timeHorizonRaw}" - skipped`);
          continue;
        } else {
          return {
            success: false,
            config,
            items: [],
            grid: [],
            summary: { totalItems: 0, byTimeHorizon: {} as Record<TimeHorizon, number>, byTheme: {} },
            error: `Row ${rowNum}: Invalid time horizon "${timeHorizonRaw}"`,
          };
        }
      }
      
      // Get strategic theme
      const strategicThemeName = String(row.strategicTheme || '').trim() || 'Other Priorities';
      const themeIndex = config.themes.findIndex(t => 
        t.name.toLowerCase() === strategicThemeName.toLowerCase()
      );
      
      // Add new theme if not found
      let theme: StrategicTheme;
      if (themeIndex === -1) {
        theme = {
          id: `theme-${config.themes.length}`,
          name: strategicThemeName,
        };
        config.themes.push(theme);
      } else {
        theme = config.themes[themeIndex];
      }
      
      // Parse order value
      const orderValue = row.order !== undefined ? Number(row.order) : (i + 1);
      
      // Create pyramid item
      const item: PyramidItem = {
        id: `import-${Date.now()}-${i}`,
        title,
        description: String(row.description || '').trim() || undefined,
        timeHorizon,
        strategicThemeId: theme.id,
        strategicThemeName: theme.name,
        position: {
          row: getRowByTimeHorizon(timeHorizon),
          col: config.themes.indexOf(theme),
        },
        source: file.name.endsWith('.csv') ? 'csv' : 'excel',
        addedAt: new Date(),
        rankInCell: orderValue, // Use the order column value
      };
      
      items.push(item);
    }
    
    // Build the grid
    const grid = buildPyramidGrid(config, items);
    
    // Calculate summary
    const summary = calculateSummary(items, config);
    
    return {
      success: true,
      config,
      items,
      grid,
      summary,
      warnings: warnings.length > 0 ? warnings : undefined,
    };
  } catch (error) {
    return {
      success: false,
      config: createDefaultPyramidConfig('Import', 'system'),
      items: [],
      grid: [],
      summary: { totalItems: 0, byTimeHorizon: {} as Record<TimeHorizon, number>, byTheme: {} },
      error: error instanceof Error ? error.message : 'Failed to parse file',
    };
  }
}

/**
 * Import from CSV content
 */
export async function importFromCSV(
  content: string,
  options: PyramidImportOptions = {}
): Promise<PyramidImportResult> {
  // Create a fake file-like object for the main import function
  const blob = new Blob([content], { type: 'text/csv' });
  const file = new File([blob], 'import.csv', { type: 'text/csv' });
  
  return importFromExcel(file, options);
}

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Normalize column names from various formats
 */
function normalizeColumnNames(
  data: Record<string, unknown>[]
): PyramidExcelRow[] {
  return data.map((row, rowIndex) => {
    const normalized: Record<string, string | number | undefined> = {};
    
    for (const [key, value] of Object.entries(row)) {
      const normalizedKey = key.toLowerCase().trim().replace(/[\s_-]+/g, '');
      
      // Map various column name formats
      if (['title', 'name', 'item', 'priority'].includes(normalizedKey)) {
        normalized.title = String(value || '');
      } else if (['description', 'desc', 'details', 'notes'].includes(normalizedKey)) {
        normalized.description = String(value || '');
      } else if (['timehorizon', 'horizon', 'tier', 'row', 'level'].includes(normalizedKey)) {
        normalized.timeHorizon = String(value || '');
      } else if (['strategictheme', 'theme', 'category', 'column', 'bucket'].includes(normalizedKey)) {
        normalized.strategicTheme = String(value || '');
      } else if (['order', 'rank', 'position', 'priority', 'seq', 'sequence'].includes(normalizedKey)) {
        // Parse order as number
        const orderValue = value;
        if (orderValue !== undefined && orderValue !== null && orderValue !== '') {
          const parsed = typeof orderValue === 'number' ? orderValue : parseInt(String(orderValue), 10);
          if (!isNaN(parsed)) {
            normalized.order = parsed;
          }
        }
      }
    }
    
    // If no order specified, use row index as implicit order (allows preserving file order)
    if (normalized.order === undefined) {
      normalized.order = rowIndex + 1;
    }
    
    return normalized as unknown as PyramidExcelRow;
  });
}

/**
 * Build the pyramid grid from items
 */
function buildPyramidGrid(
  config: PyramidConfig,
  items: PyramidItem[]
): PyramidCell[][] {
  const grid = createEmptyPyramidGrid(config);
  
  for (const item of items) {
    const { row, col } = item.position;
    if (row >= 0 && row < 3 && col >= 0 && col < config.themes.length) {
      grid[row][col].items.push(item);
    }
  }
  
  // Sort items within each cell by rankInCell (ascending = highest priority first)
  for (const row of grid) {
    for (const cell of row) {
      cell.items.sort((a, b) => {
        const rankA = a.rankInCell ?? Number.MAX_SAFE_INTEGER;
        const rankB = b.rankInCell ?? Number.MAX_SAFE_INTEGER;
        return rankA - rankB;
      });
    }
  }
  
  return grid;
}

/**
 * Calculate summary statistics for the import
 */
function calculateSummary(
  items: PyramidItem[],
  _config: PyramidConfig
): PyramidImportResult['summary'] {
  const byTimeHorizon: Record<TimeHorizon, number> = {
    create_the_future: 0,
    reinforce_foundation: 0,
    get_house_in_order: 0,
  };
  
  const byTheme: Record<string, number> = {};
  
  for (const item of items) {
    byTimeHorizon[item.timeHorizon]++;
    byTheme[item.strategicThemeName] = (byTheme[item.strategicThemeName] || 0) + 1;
  }
  
  return {
    totalItems: items.length,
    byTimeHorizon,
    byTheme,
  };
}

// ============================================================================
// Export to Focus Lists
// ============================================================================

export interface FocusListOutput {
  name: string;
  description: string;
  items: Array<{
    id: string;
    title: string;
    description?: string;
    metadata: Record<string, unknown>;
  }>;
}

/**
 * Convert pyramid items to focus list format
 */
export function convertToFocusLists(
  result: PyramidImportResult,
  outputStructure: 'single_list' | 'per_theme' | 'per_tier',
  namePrefix: string = 'Pyramid'
): FocusListOutput[] {
  if (!result.success || result.items.length === 0) {
    return [];
  }
  
  switch (outputStructure) {
    case 'single_list':
      return [{
        name: namePrefix,
        description: `All items from the Max Priorities Pyramid (${result.items.length} items)`,
        items: result.items.map(item => ({
          id: item.id,
          title: item.title,
          description: item.description,
          metadata: {
            timeHorizon: item.timeHorizon,
            strategicTheme: item.strategicThemeName,
            row: item.position.row,
            col: item.position.col,
          },
        })),
      }];
    
    case 'per_theme':
      return result.config.themes.map(theme => ({
        name: `${namePrefix} - ${theme.name}`,
        description: `${theme.name} priorities from the Max Priorities Pyramid`,
        items: result.items
          .filter(item => item.strategicThemeName === theme.name)
          .map(item => ({
            id: item.id,
            title: item.title,
            description: item.description,
            metadata: {
              timeHorizon: item.timeHorizon,
              row: item.position.row,
            },
          })),
      })).filter(list => list.items.length > 0);
    
    case 'per_tier':
      return TIME_HORIZONS_ORDERED.map(horizon => {
        const horizonItems = result.items.filter(item => item.timeHorizon === horizon);
        return {
          name: `${namePrefix} - ${horizonItems[0]?.timeHorizon === 'create_the_future' ? 'Create the Future' : 
                 horizonItems[0]?.timeHorizon === 'reinforce_foundation' ? 'Reinforce Foundation' : 
                 'Get House in Order'}`,
          description: `${horizon.replace(/_/g, ' ')} priorities from the Max Priorities Pyramid`,
          items: horizonItems.map(item => ({
            id: item.id,
            title: item.title,
            description: item.description,
            metadata: {
              strategicTheme: item.strategicThemeName,
              col: item.position.col,
            },
          })),
        };
      }).filter(list => list.items.length > 0);
    
    default:
      return [];
  }
}

// ============================================================================
// Export Functions
// ============================================================================

export interface PyramidExportData {
  version: number;
  exportedAt: string;
  name: string;
  categories: string[];
  items: Array<{
    id: string;
    name: string;
    description?: string;
    category: string;
    timeHorizon: string;
    rank: number;
  }>;
}

/**
 * Export pyramid data to Excel file
 */
export function exportPyramidToExcel(
  items: Array<{
    id: string;
    name: string;
    description?: string;
    category: string;
    timeHorizon: string;
    rank: number;
  }>,
  categories: string[],
  pyramidName: string = 'My Pyramid'
): void {
  const workbook = XLSX.utils.book_new();
  
  // Convert items to export format
  const exportData = items.map(item => ({
    'Title': item.name,
    'Description': item.description || '',
    'Time Horizon': item.timeHorizon === 'Transform' ? 'Create the Future' :
                    item.timeHorizon === 'Grow' ? 'Reinforce Foundation' :
                    'Get House in Order',
    'Strategic Theme': item.category,
    'Order': item.rank,
  }));
  
  // Sort by time horizon priority, then theme, then rank
  const horizonOrder = { 'Create the Future': 0, 'Reinforce Foundation': 1, 'Get House in Order': 2 };
  exportData.sort((a, b) => {
    const horizonDiff = (horizonOrder[a['Time Horizon'] as keyof typeof horizonOrder] || 0) - 
                        (horizonOrder[b['Time Horizon'] as keyof typeof horizonOrder] || 0);
    if (horizonDiff !== 0) return horizonDiff;
    
    const themeDiff = categories.indexOf(a['Strategic Theme']) - categories.indexOf(b['Strategic Theme']);
    if (themeDiff !== 0) return themeDiff;
    
    return a['Order'] - b['Order'];
  });
  
  const itemsSheet = XLSX.utils.json_to_sheet(exportData);
  itemsSheet['!cols'] = [
    { wch: 40 }, // Title
    { wch: 60 }, // Description
    { wch: 25 }, // Time Horizon
    { wch: 25 }, // Strategic Theme
    { wch: 10 }, // Order
  ];
  XLSX.utils.book_append_sheet(workbook, itemsSheet, 'Items');
  
  // Add strategic themes sheet
  const themesData = categories.map((theme, idx) => ({
    'Theme Name': theme,
    'Column Order': idx + 1,
  }));
  const themesSheet = XLSX.utils.json_to_sheet(themesData);
  themesSheet['!cols'] = [{ wch: 25 }, { wch: 15 }];
  XLSX.utils.book_append_sheet(workbook, themesSheet, 'Strategic Themes');
  
  // Generate and download
  const buffer = XLSX.write(workbook, { type: 'array', bookType: 'xlsx' });
  const blob = new Blob([buffer], { 
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' 
  });
  
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${pyramidName.replace(/[^a-zA-Z0-9]/g, '-')}-pyramid.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

/**
 * Export pyramid data to JSON file
 */
export function exportPyramidToJSON(
  items: Array<{
    id: string;
    name: string;
    description?: string;
    category: string;
    timeHorizon: string;
    rank: number;
  }>,
  categories: string[],
  pyramidName: string = 'My Pyramid'
): void {
  const exportData: PyramidExportData = {
    version: 1,
    exportedAt: new Date().toISOString(),
    name: pyramidName,
    categories,
    items: items.map(item => ({
      id: item.id,
      name: item.name,
      description: item.description,
      category: item.category,
      timeHorizon: item.timeHorizon,
      rank: item.rank,
    })),
  };
  
  const blob = new Blob([JSON.stringify(exportData, null, 2)], { type: 'application/json' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${pyramidName.replace(/[^a-zA-Z0-9]/g, '-')}-pyramid.json`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
}

// ============================================================================
// Local Storage Persistence
// ============================================================================
// 
// DEPRECATED: These localStorage functions are kept for backwards compatibility
// and ephemeral UI state only. Per Build Contract Section 2.2, pyramid data 
// should be persisted to Supabase via the pyramidStore.
// 
// Use usePyramidStore for durable persistence instead.
// ============================================================================

const PYRAMID_STORAGE_KEY = 'peak_priorities_pyramid';

export interface SavedPyramidState {
  version: number;
  savedAt: string;
  name: string;
  categories: string[];
  items: Array<{
    id: string;
    name: string;
    description?: string;
    category: string;
    timeHorizon: string;
    rank: number;
  }>;
}

/**
 * @deprecated Use usePyramidStore for Supabase persistence instead.
 * Save pyramid state to localStorage (ephemeral only)
 */
export function savePyramidToLocalStorage(
  items: Array<{
    id: string;
    name: string;
    description?: string;
    category: string;
    timeHorizon: string;
    rank: number;
  }>,
  categories: string[],
  pyramidName: string = 'My Pyramid'
): boolean {
  try {
    const state: SavedPyramidState = {
      version: 1,
      savedAt: new Date().toISOString(),
      name: pyramidName,
      categories,
      items: items.map(item => ({
        id: item.id,
        name: item.name,
        description: item.description,
        category: item.category,
        timeHorizon: item.timeHorizon,
        rank: item.rank,
      })),
    };
    localStorage.setItem(PYRAMID_STORAGE_KEY, JSON.stringify(state));
    return true;
  } catch (error) {
    console.error('Failed to save pyramid to localStorage:', error);
    return false;
  }
}

/**
 * Load pyramid state from localStorage
 */
export function loadPyramidFromLocalStorage(): SavedPyramidState | null {
  try {
    const stored = localStorage.getItem(PYRAMID_STORAGE_KEY);
    if (!stored) return null;
    
    const state = JSON.parse(stored) as SavedPyramidState;
    
    // Validate the structure
    if (!state.version || !Array.isArray(state.items) || !Array.isArray(state.categories)) {
      console.warn('Invalid pyramid state structure in localStorage');
      return null;
    }
    
    return state;
  } catch (error) {
    console.error('Failed to load pyramid from localStorage:', error);
    return null;
  }
}

/**
 * Clear pyramid state from localStorage
 */
export function clearPyramidFromLocalStorage(): void {
  localStorage.removeItem(PYRAMID_STORAGE_KEY);
}

/**
 * Check if there's a saved pyramid in localStorage
 */
export function hasSavedPyramid(): boolean {
  return localStorage.getItem(PYRAMID_STORAGE_KEY) !== null;
}

export default {
  generatePyramidTemplate,
  downloadPyramidTemplate,
  importFromExcel,
  importFromCSV,
  convertToFocusLists,
  exportPyramidToExcel,
  exportPyramidToJSON,
  savePyramidToLocalStorage,
  loadPyramidFromLocalStorage,
  clearPyramidFromLocalStorage,
  hasSavedPyramid,
};
